import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  User,
  UserCreateFormData,
  UserUpdateFormData,
} from "../../schemas/userSchema";

export interface UserResult {
  users: User[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export type UsersResponse = UserResult;

export interface UserFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateUserPayload {
  id: string;
  user: UserUpdateFormData;
}

const GET_USERS = gql`
  query GetUsers($page: Int, $pageSize: Int, $search: String) {
    users(page: $page, pageSize: $pageSize, search: $search) {
      users {
        id
        name
        username
        email
        tenantId
        roleId
        phoneNumber
        isActive
        createdAt
        updatedAt
        role
        permissions
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_USER_BY_ID = gql`
  query GetUserById($id: ID!) {
    user(id: $id) {
      id
      name
      username
      email
      tenantId
      roleId
      phoneNumber
      isActive
      createdAt
      updatedAt
      role
      permissions
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input)
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input)
  }
`;

const DELETE_USERS = gql`
  mutation DeleteUsers($ids: [ID!]!) {
    deleteUsers(ids: $ids)
  }
`;

export const userService = {
  getAll: async (filters: UserFilters = {}): Promise<UserResult> => {
    const { data } = await client.query({
      query: GET_USERS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.users;
  },

  getById: async (id: string): Promise<User> => {
    const { data } = await client.query({
      query: GET_USER_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.user;
  },

  create: async (input: UserCreateFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_USER,
      variables: { input },
    });
    return data.createUser;
  },

  update: async (payload: UpdateUserPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_USER,
      variables: {
        id: payload.id,
        input: payload.user,
      },
    });
    return data.updateUser;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_USERS,
      variables: { ids },
    });
    return data.deleteUsers;
  },
};
